import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';

/**
 * Retrieve all currencies
 * @param {Object} params - The parameters for the request
 * @returns {Promise<Array>} - A promise that resolves to an array of currencies
 */
export const retrieveAllCurrencies = async (params) => {
  try {
    const response = await axiosClient.get(`/calculators/currencies`, {
      params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response?.data ?? [];
  } catch (error) {
    console.error(
      `Failed to retrieve currencies, date: ${params?.date}:`,
      error
    );
    throw error;
  }
};

/**
 * Create a new calculation
 * @param {*} data
 * @returns
 */
export const createNewCalculation = async (data) => {
  try {
    const response = await axiosClient.post(`/calculators/create`, data);
    return response?.data ?? [];
  } catch (error) {
    console.error(`Failed to create new calculation:`, error);
    throw error;
  }
};

/**
 * Get currency exchange rate by date
 * @param {string} currency - e.g. "eur"
 * @param {string} date - format "YYYY-MM-DD"
 * @returns {Promise<Object>} - currency data
 */
export const getCurrencyByDate = async (currency, date) => {
  try {
    const response = await axiosClient.get(
      `/calculators/currencies/${currency}?date=${date}`
    );
    return response?.data ?? {};
  } catch (error) {
    console.error(
      `Failed to fetch currency data for ${currency} on ${date}:`,
      error
    );
    throw error;
  }
};

/**
 * Converts a given amount from one currency to another.
 *
 * @async
 * @function
 * @param {Object} params - The parameters for the currency conversion.
 * @param {string} params.from - The source currency code (e.g., 'USD').
 * @param {string} params.to - The target currency code (e.g., 'EUR').
 * @param {number} params.amount - The amount to be converted.
 * @returns {Promise<number>} The converted amount, or 0 if unavailable.
 */
export const currencyConversations = async ({ from, to, amount }) => {
  const response = await axiosClient.get(
    `/calculators/currencies/convert-between`,
    {
      params: {
        from,
        to,
        amount,
      },
    }
  );

  return response?.data?.convertedAmount ?? 0;
};

/**
 * Returns RSD per 1 EUR using the backend convert-between endpoint (optional date).
 * If `date` is provided and backend supports it, it will be forwarded as a query param.
 * @param {string} [date] - Optional date in "YYYY-MM-DD"
 * @returns {Promise<number>} - NBS middle rate (RSD for 1 EUR) or 0.
 */
export const retrieveNBSEurRate = async (date) => {
  try {
    const response = await axiosClient.get(
      `/calculators/currencies/convert-between`,
      {
        params: {
          from: 'EUR',
          to: 'RSD',
          amount: 1,
          ...(date ? { date } : {}),
        },
        paramsSerializer: (p) =>
          qs.stringify(p, { encode: false, arrayFormat: 'brackets' }),
      }
    );

    const val =
      response?.data?.convertedAmount ??
      response?.data?.data?.convertedAmount ??
      null;

    return Number(val) || 0;
  } catch (error) {
    console.error(`Failed to retrieve NBS EUR rate:`, error);
    return 0;
  }
};

const CalculatorsService = {
  retrieveAllCurrencies,
  createNewCalculation,
  getCurrencyByDate,
  currencyConversations,
  retrieveNBSEurRate
};

export default CalculatorsService;
